import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

/**
 * Reset-password page — landing page for the email link.
 * URL: /reset-password?token=<token>
 */
const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useSEO({ title: "Reset password · GOYA LISBOA", noindex: true });

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setError(null);
    setLoading(true);
    try {
      const data = await confirmPasswordReset(token, password);
      localStorage.setItem("gl_token", data.token);
      setDone(true);
      setTimeout(() => navigate("/account", { replace: true }), 1600);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen pt-28 md:pt-36 pb-24 px-6 md:px-12 max-w-md mx-auto"
      data-testid="reset-password-page"
    >
      <div className="font-badge text-[10px] tracking-[0.32em] uppercase text-[var(--gl-orange-hover)]">
        {done ? "ALL SET" : "PASSWORD RESET"}
      </div>
      <h1 className="font-serif-display text-4xl mt-3 tracking-tight text-[#241C18]">
        {done ? "Signed in — welcome back." : "Choose a new password."}
      </h1>

      {done ? (
        <p className="mt-6 text-sm text-[#8A7568]">Redirecting you to your account…</p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
          <input
            type="password"
            className="input-editorial"
            placeholder="New password (6+ characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-input"
            required
            minLength={6}
          />
          <input
            type="password"
            className="input-editorial"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-password-confirm"
            required
            minLength={6}
          />
          {error && (
            <div className="text-sm text-[#C97A63] font-typewriter tracking-wider" data-testid="reset-password-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !token}
            className="btn-ink w-full"
            data-testid="reset-password-submit"
          >
            {loading ? "Saving…" : "Save new password"}
          </button>
          <p className="text-center text-xs text-[#8A7568]">
            <Link to="/" className="underline">Back to the shop</Link>
          </p>
        </form>
      )}
    </div>
  );
};

export default ResetPassword;
